import { KalturaAssetType, KalturaPpvPrice, KalturaPurchaseStatus } from '@kaltura-ott/ott-client-types/api/types';
import {
  assetToType,
  filesGetProductPrices,
  findNotFoundForLinearIdList,
  FormatKalturaProductPricePpv,
  formatKalturaProductPricePpv,
  FormatProductPrice,
  getAssetsByExternalIds,
  getAssetsByFilter,
  getBookmarks,
  getChannelLogoList,
  getCurrentProgramByLinearList,
  getLinearById,
  getPpvByFileIds,
  getProductPrices,
  getPurchaseCache,
  KuxEpisodeType,
  KuxLinearType,
  KuxLinkAsAssetType,
  KuxLogo,
  KuxMediaAssetType,
  KuxMenuItem,
  KuxMovieType,
  KuxPackage,
  KuxPerson,
  KuxPpv,
  KuxProgramType,
  KuxPurchaseInfo,
  KuxSeriesType,
  KuxSubscription,
  mediaFileGetSubscription,
  MediaType,
  PERFORMANCE_FEATURE,
  PRODUCT_PRICE_TYPES,
  PurchaseCache,
  subscriptionsGetAssets,
  subscriptionsGetProductPrices,
  getAllCachedPrograms,
} from '@kaltura-ott/tvpil';
import { lastValueFrom, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { container } from 'tsyringe';

import { assertPresence } from '../../utils/error-handling';
import { featureEnabler } from '../../utils/feature-enabler';
import type { DmsStore } from '../dms/dms-store';
import type { LinearRepository } from '../linearRepository/linear-repository';
import { SubscriptionsRepository, SubscriptionType } from '../subscriptionsRepository';

import { EntitiesType } from './entities.types';
import { Episode } from './Episode';
import type { Linear } from './Linear';
import { LinkAsAsset } from './LinkAsAsset';
import { Logo } from './Logo';
import { Movie } from './Movie';
import { Package } from './Package';
import { Person } from './Person';
import { PlayableAsset } from './PlayableAsset';
import { Ppv } from './Ppv';
import { Program } from './Program';
import { Record as Recording } from './Record';
import { Series } from './Series';
import { Subscription } from './Subscription';
import { VodAsset } from './VodAsset';

export function creatorEntityStore(kuxAsset: KuxProgramType): Program;
export function creatorEntityStore(kuxAsset: KuxLinearType): Linear;
export function creatorEntityStore(kuxAsset: KuxLinkAsAssetType): LinkAsAsset;
export function creatorEntityStore(kuxAsset: KuxMovieType): Movie;
export function creatorEntityStore(kuxAsset: KuxEpisodeType): Episode;
export function creatorEntityStore(kuxAsset: KuxSeriesType): Series;
export function creatorEntityStore(kuxAsset: KuxMediaAssetType): EntitiesType {
  const dmsStore = container.resolve<DmsStore>('dmsStore');
  const dms = assertPresence(dmsStore.dms);
  const linearRepository = container.resolve<LinearRepository>('linearRepository');

  switch (kuxAsset.type.toString()) {
    case dms.mediaTypes.Program: {
      const entity = new Program(kuxAsset as KuxProgramType);
      entity.init(kuxAsset as KuxProgramType);
      return entity;
    }
    case dms.mediaTypes.Linear:
      return linearRepository.getLinearById(kuxAsset.id)!;
    case dms.mediaTypes.in_application_link_asset:
      return new LinkAsAsset(kuxAsset as KuxLinkAsAssetType);
    case dms.mediaTypes.Movie:
      return new Movie(kuxAsset as KuxMovieType);
    case dms.mediaTypes.Episode:
      return new Episode(kuxAsset as KuxEpisodeType);
    case dms.mediaTypes.Series:
      return new Series(kuxAsset as KuxSeriesType);
    case dms.mediaTypes.Person:
      return new Person(kuxAsset as KuxPerson);
    case dms.mediaTypes.Logo:
      return new Logo(kuxAsset as KuxLogo);
    default:
      throw new Error(`${kuxAsset.type.toString()} - wrong entity`);
  }
}

export const createPpvEntity = (kuxAsset: KuxPpv, mediaFileId: number, assetId: number): Ppv => {
  return new Ppv(kuxAsset, mediaFileId, assetId);
};

export const createLinkAsAssetByKuxMenuItem = (kuxMenu: KuxMenuItem): LinkAsAsset => {
  const dmsStore = container.resolve<DmsStore>('dmsStore');
  const linkAsAssetType: KuxLinkAsAssetType = {
    id: kuxMenu.id,
    images: kuxMenu.images,
    title: kuxMenu.name,
    adult: false,
    parentalRating: 0,
    type: Number(dmsStore.dms.mediaTypes.in_application_link_asset),
    metas: {},
    tags: {},
    mediaType: MediaType.in_application_link_asset,
    linkItem: kuxMenu,
  };

  return new LinkAsAsset(linkAsAssetType);
};

export const createSubscriptionEntity = (
  kuxAsset: KuxSubscription,
  asset: Package,
  purchaseInfo?: KuxPurchaseInfo,
): Subscription => {
  return new Subscription(kuxAsset, asset, purchaseInfo);
};

export const createPackageEntity = (kuxAsset: KuxPackage): Package => {
  return new Package(kuxAsset);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const addLinearsToCatchupSeries = async (program: Program): Promise<any> => {
  if (!program.data.seriesId) {
    return;
  }
  const linearIdList = await lastValueFrom(getChannelLogoList(program.data.seriesId));
  program.linearIdList = linearIdList;
};

export const findProductPriceByMediaFileId = (
  productPrices: FormatKalturaProductPricePpv[],
  mediaFileId?: number,
): { price: FormatKalturaProductPricePpv | undefined; isPurchased: boolean } | undefined => {
  if (!mediaFileId || !productPrices.length) return;
  const purchased = productPrices.find((productPrice) => {
    const isPurchased = ![
      KalturaPurchaseStatus.forPurchase,
      KalturaPurchaseStatus.forPurchaseSubscriptionOnly,
      KalturaPurchaseStatus.pendingEntitlement,
    ].includes(productPrice.purchaseStatus);

    if (isPurchased && productPrice.fileId === mediaFileId) return productPrices;

    return undefined;
  });

  const unPurchased = productPrices.find((productPrice) => productPrice.fileId === mediaFileId);

  return purchased ? { price: purchased, isPurchased: true } : { price: unPurchased, isPurchased: false };
};

export const cachedPurchaseToPurchaseStatus = <T extends FormatProductPrice>(
  cachedPrices: PurchaseCache[],
  price: T,
) => {
  cachedPrices.forEach((cachedPrice) => {
    if (cachedPrice.purchaseState === 'OK') {
      if (price.purchaseStatus === KalturaPurchaseStatus.forPurchase) {
        price.purchaseStatus = KalturaPurchaseStatus.ppvPurchased;
        if (cachedPrice.endDate) {
          price.endDate = cachedPrice.endDate;
        }
        return;
      } else if (price.purchaseStatus === KalturaPurchaseStatus.forPurchaseSubscriptionOnly) {
        price.purchaseStatus = KalturaPurchaseStatus.subscriptionPurchased;
        return;
      }
    } else if (cachedPrice.purchaseState === 'Pending') {
      price.purchaseStatus = KalturaPurchaseStatus.pendingEntitlement;
      return;
    }
  });

  return price;
};

export const checkCachedPurchase = async (
  price: FormatKalturaProductPricePpv | FormatProductPrice,
  playableItem?: PlayableAsset,
  subscriptionIds?: string[],
): Promise<PurchaseCache[]> => {
  let cachedPrices: PurchaseCache[] = [];
  if (
    price &&
    [
      KalturaPurchaseStatus.free,
      KalturaPurchaseStatus.collectionPurchased,
      KalturaPurchaseStatus.subscriptionPurchased,
      KalturaPurchaseStatus.ppvPurchased,
      KalturaPurchaseStatus.pendingEntitlement,
    ].includes(price.purchaseStatus)
  ) {
    return (cachedPrices = []);
  }

  if (playableItem) {
    const cachePpv = await getPurchaseCache(playableItem.mediaFileId);
    if (cachePpv) {
      cachedPrices.push(cachePpv);
    }
  } else {
    if (subscriptionIds?.length) {
      await Promise.all(
        subscriptionIds.map(async (id) => {
          const cacheSub = await getPurchaseCache(undefined, id);
          if (cacheSub) {
            cachedPrices.push(cacheSub);
          }
        }),
      );
    }
  }

  return cachedPrices;
};

export const addPurchaseStatusToPlayable = async (playableItems: PlayableAsset[]): Promise<PlayableAsset[]> => {
  const mediaFileIdList = playableItems
    .map((playableItem) => {
      return playableItem.mediaFileId;
    })
    .filter(Boolean) as number[];

  const productPrices = await lastValueFrom(filesGetProductPrices(mediaFileIdList));

  return playableItems.map((playableItem) => {
    const price = findProductPriceByMediaFileId(productPrices, playableItem.mediaFileId)?.price;

    playableItem.setPrice(price);
    return playableItem;
  });
};

export const addPriceToPurchasedPlayable = async (playableItem: PlayableAsset) => {
  if (!playableItem.mediaFileId) {
    return;
  }

  const productPrices = await lastValueFrom(filesGetProductPrices([playableItem.mediaFileId]));
  const priceDetails = findProductPriceByMediaFileId(productPrices, playableItem.mediaFileId);

  if (priceDetails?.isPurchased) {
    playableItem.setPrice(priceDetails.price);
    return playableItem;
  }
};

export const addCachedPurchaseToPlayable = async (playableItem: PlayableAsset): Promise<PlayableAsset> => {
  if (!playableItem.mediaFileId) {
    return playableItem;
  }
  const productPrices = await lastValueFrom(filesGetProductPrices([playableItem.mediaFileId]));
  const subscriptionIds = playableItem.subscriptionIds;
  const price = findProductPriceByMediaFileId(productPrices, playableItem.mediaFileId)?.price;

  if (price) {
    const cachedPurchase = await checkCachedPurchase(price, playableItem, subscriptionIds);
    playableItem.setPrice(cachedPurchaseToPurchaseStatus(cachedPurchase, price));
  }

  return playableItem;
};

export const checkProductPriceStatus = async (playableItem: PlayableAsset) => {
  const fileIds = playableItem.mediaFileId ? [playableItem.mediaFileId] : undefined;
  if (!fileIds) {
    return;
  }
  return lastValueFrom(
    getProductPrices(PRODUCT_PRICE_TYPES.file, fileIds).pipe(
      map(async (productPrices) => {
        if (!productPrices || !productPrices.length) return;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const price = findProductPriceByMediaFileId(productPrices as any, playableItem.mediaFileId)?.price;

        if (price) {
          const subscriptionIds = playableItem.subscriptionIds;
          const cachedPurchase = await checkCachedPurchase(price, playableItem, subscriptionIds);
          if (cachedPurchase.length > 0) {
            return;
          }
        }

        const cachedPrice = playableItem.price;
        if (cachedPrice && cachedPrice.purchaseStatus !== price?.purchaseStatus) {
          playableItem.setPrice(formatKalturaProductPricePpv(price as KalturaPpvPrice));
          return true;
        }
        return;
      }),
    ),
  );
};

export const filterPurchaseStatusToProgram = async (playableItems: Program[]): Promise<PlayableAsset[]> => {
  const notRecordPrograms = playableItems.filter((asset) => !asset.isProgramRecord);
  await addPurchaseStatusToPlayable(notRecordPrograms);
  playableItems = playableItems.filter((playableItem) => playableItem.isPurchased);
  return playableItems;
};

export const filterPurchaseStatusToLinear = async (linears: Linear[]): Promise<Linear[]> => {
  if (
    !featureEnabler.variation(
      'PERFORMANCE_PRODUCT_PRICE_HERO_EPG',
      PERFORMANCE_FEATURE.PERFORMANCE_PRODUCT_PRICE_HERO_EPG,
    )
  ) {
    return linears;
  }

  await addPurchaseStatusToPlayable(linears);
  linears = linears.filter((linear) =>
    linear.price?.purchaseStatus
      ? [
          KalturaPurchaseStatus.ppvPurchased,
          KalturaPurchaseStatus.free,
          KalturaPurchaseStatus.subscriptionPurchased,
          KalturaPurchaseStatus.subscriptionPurchasedWrongCurrency,
          KalturaPurchaseStatus.prePaidPurchased,
          KalturaPurchaseStatus.collectionPurchased,
        ].includes(linear.price?.purchaseStatus) || linear.data.representPackage
      : undefined,
  );
  return linears;
};

interface PlayableBookmark {
  assetId: number;
  position: number;
  finishedWatching: boolean;
}

export const addBookmarkToPlayable = async (
  playableItems: PlayableAsset[],
  assetTypeEqual = KalturaAssetType.epg,
): Promise<PlayableAsset[]> => {
  const bookmarks: PlayableBookmark[] = await lastValueFrom(
    getBookmarks(playableItems.map((playable) => playable.data.id).join(','), assetTypeEqual).pipe(
      map((assetList) => {
        const assets = assetList.objects;
        const items = assets.map((item) => ({
          assetId: Number(item.id),
          position: item.position,
          finishedWatching: item.finishedWatching,
        }));
        return items;
      }),
      catchError((error) => {
        return of(error);
      }),
    ),
  );

  return playableItems.map((playableItem) => {
    const playableBookmark = bookmarks.find((bookmark) => {
      return bookmark.assetId === playableItem.data.id;
    });
    if (playableBookmark) {
      playableItem.bookmark = {
        ...playableBookmark,
        duration: (playableItem.data as KuxProgramType).duration / 1000,
      };
    }
    return playableItem;
  });
};

export const mapLinearsToCurrentKuxPrograms = (
  linears: Linear[],
  programs: KuxProgramType[],
  setEmpty = true,
  loadMosaicPrograms = false,
): { linears: Linear[]; notFoundLinears: Linear[] } => {
  const dmsStore = container.resolve<DmsStore>('dmsStore');
  const notFoundLinears: Linear[] = [];
  linears.forEach((linear) => {
    let currentProgram = programs?.find(
      (program) =>
        program.linearAssetId === linear.data.id ||
        // @ts-expect-error TODO Fix
        program.mosaicInfo.sourceLinearAssetId === linear.data.id ||
        program.permanentProgram?.linearAssetId === linear.data.id,
    );

    if (currentProgram) {
      if (!loadMosaicPrograms && dmsStore.dms.mosaicInfo.isMosaicSupported && currentProgram.permanentProgram) {
        currentProgram = currentProgram.permanentProgram;
      }

      linear.setupCurrentProgram(currentProgram);
    } else {
      if (setEmpty) {
        linear.currentProgram = undefined;
      }
      notFoundLinears.push(linear);
    }
  });
  return { linears, notFoundLinears };
};

export const addPermanentProgramsToMosaicPrograms = async (programs: KuxProgramType[]): Promise<KuxProgramType[]> => {
  const programsToModify = programs.filter((it) => !it.permanentProgram);
  const permanentExternalIdsMap = programsToModify.reduce(
    (acc, item: KuxProgramType) => {
      if (
        item.mosaicInfo?.isMosaicEvent === '1' &&
        (item.mosaicInfo?.type === 'hidden' || item.mosaicInfo?.type === 'mosaic') &&
        item.mosaicInfo?.sourceProgramExternalId
      ) {
        acc[item.mosaicInfo.sourceProgramExternalId] = item.externalId;
      }

      return acc;
    },
    {} as Record<string, string>,
  );
  const permanentExternalIds = Object.keys(permanentExternalIdsMap);

  if (permanentExternalIds.length) {
    const cached = getAllCachedPrograms();
    let permanentProgramTypes: (KuxProgramType | undefined)[] = cached.filter(
      (it: KuxProgramType) => !!permanentExternalIdsMap[it.externalId],
    );

    if (!permanentProgramTypes || permanentProgramTypes.length !== permanentExternalIds.length) {
      permanentProgramTypes =
        (await lastValueFrom(getAssetsByExternalIds<KuxProgramType>(permanentExternalIds as string[]))) || [];
    }

    if (permanentProgramTypes) {
      programsToModify.map((program) => {
        const permanentProgram = permanentProgramTypes.find(
          (permanent) => permanent?.externalId === program.mosaicInfo?.sourceProgramExternalId,
        );

        if (permanentProgram) {
          program.permanentProgram = permanentProgram;
        }

        return program;
      }) as KuxProgramType[];
    }
  }

  return programs;
};

export const addCurrentProgramToLinear = async (
  linears: Linear[],
  liveTimestamp = Date.now(),
  withNotFound = false,
  loadMosaicPrograms = false,
): Promise<Linear[]> => {
  const programIdList = linears.map((linear) => linear.data.id);
  const dmsStore = container.resolve<DmsStore>('dmsStore');
  const programs = await lastValueFrom(
    getCurrentProgramByLinearList({
      linearIdList: programIdList,
      liveTimestamp,
      allDayEpg: false,
      loadMosaicPrograms: dmsStore.dms.mosaicInfo.isMosaicSupported,
    }),
    {
      defaultValue: [],
    },
  );
  const { notFoundLinears } = mapLinearsToCurrentKuxPrograms(linears, programs, !withNotFound, loadMosaicPrograms);
  if (withNotFound) {
    const programs = await lastValueFrom(
      findNotFoundForLinearIdList(
        notFoundLinears.map((linear) => linear.data.id),
        liveTimestamp,
      ),
    );
    mapLinearsToCurrentKuxPrograms(notFoundLinears, programs, true, loadMosaicPrograms);
  }
  await addPermanentProgramsToMosaicPrograms(
    linears.map((it) => it.currentProgram && it.currentProgram?.data).filter(Boolean) as KuxProgramType[],
  );
  return linears;
};

export const addBookmarkToRecords = async (programsInPage: Program[]): Promise<Recording[]> => {
  const records: Recording[] = [];
  const recordIdList: number[] = [];
  const durations: { [recordId: string]: number } = {};
  programsInPage.forEach((program) => {
    if (program.record?.data) {
      records.push(program.record);
      recordIdList.push(program.record.data.id);
      durations[program.record.data.id] = program.data.duration;
    }
  });

  const bookmarks: PlayableBookmark[] = await lastValueFrom(
    getBookmarks(recordIdList.filter(Boolean).join(','), KalturaAssetType.recording).pipe(
      map((assetList) => {
        const assets = assetList.objects;
        const items = assets.map((item) => ({
          assetId: Number(item.id),
          position: item.position,
          finishedWatching: item.finishedWatching,
        }));
        return items;
      }),
      catchError((error) => {
        return of(error);
      }),
    ),
  );

  return records.map((record) => {
    const recordBookmark = bookmarks.find((bookmark) => {
      return bookmark?.assetId === record.data.id;
    });

    if (recordBookmark) {
      record.bookmark = {
        ...recordBookmark,
        duration: durations[record.data.id] / 1000,
      };
    }
    return record;
  });
};

export const filterAdultChannel = (items: PlayableAsset[]) => items.filter((item: PlayableAsset) => !item.isAdult);

export const getLinearByAssetId = async (assetId: number[]): Promise<Linear[] | undefined> => {
  if (!assetId) {
    return;
  }
  const assets = await lastValueFrom(getLinearById(assetId));
  return assets
    .map((asset) => {
      if (asset) {
        return creatorEntityStore(asset);
      }
    })
    .filter(Boolean) as Linear[];
};

export const addRentPossibilitiesToPlayable = async (items: VodAsset[]): Promise<VodAsset[]> => {
  await Promise.all(
    items.map(async (item) => {
      if (item.mediaFileId) {
        const rents = await lastValueFrom(getPpvByFileIds(item.mediaFileId));
        item.rents = rents
          .map((rent: KuxPpv | undefined) => {
            if (rent && item.mediaFileId && !rent.isSubscriptionOnly) {
              return createPpvEntity(rent, item.mediaFileId, item.id);
            }
          })
          .filter(Boolean) as Ppv[];
      }
      return item;
    }),
  );

  return items;
};

export interface SelectedSubscription {
  packageId: number;
  baseTitle?: string;
  addonTitle?: string;
  amount: number;
  type: SubscriptionType;
}

const selectSubscription = async (
  type: SubscriptionType,
  subscriptions?: KuxSubscription[],
  price?: FormatKalturaProductPricePpv,
): Promise<SelectedSubscription | undefined> => {
  const dmsStore = container.resolve<DmsStore>('dmsStore');

  if (subscriptions?.length) {
    const assetResult = await lastValueFrom(
      subscriptionsGetAssets([subscriptions[0].id], dmsStore.dms?.mediaTypes.Package, ''),
    );

    if (assetResult[0]) {
      const selectedSubscription = createSubscriptionEntity(subscriptions[0], createPackageEntity(assetResult[0]), {
        purchaseStatus: price?.purchaseStatus,
      });

      const baseTitle =
        type === SubscriptionType.base
          ? selectedSubscription.packageData.title
          : selectedSubscription.basePlan?.packageData.title;
      const addonTitle = type !== SubscriptionType.base ? selectedSubscription.packageData.title : undefined;

      return {
        packageId: selectedSubscription.packageData.id,
        baseTitle: baseTitle,
        addonTitle: addonTitle,
        amount: subscriptions.length,
        type,
      };
    }
  }
};

export const addSubscriptionPossibilitiesToPlayable = async (
  item: PlayableAsset,
  price?: FormatKalturaProductPricePpv,
): Promise<PlayableAsset | undefined> => {
  const subscriptionRepository = container.resolve(SubscriptionsRepository);

  if (!item.mediaFileId) return;

  const subscriptionResult = await lastValueFrom(mediaFileGetSubscription(item.mediaFileId));

  item.subscriptionIds = subscriptionResult.map((subscription) => subscription.id);

  if (subscriptionResult.length > 0) {
    if (subscriptionRepository.availableAddonSubscriptionsForPurchasedBase(subscriptionResult)?.length) {
      item.selectedSubscription = await selectSubscription(
        SubscriptionType.addonForPurchasedBase,
        subscriptionRepository.availableAddonSubscriptionsForPurchasedBase(subscriptionResult),
        price,
      );
      return item;
    } else if (subscriptionRepository.availableBaseSubscriptions(subscriptionResult).length) {
      item.selectedSubscription = await selectSubscription(
        SubscriptionType.base,
        subscriptionRepository.availableBaseSubscriptions(subscriptionResult),
        price,
      );
      return item;
    } else if (subscriptionRepository.availableAddonSubscriptionsForOtherBase(subscriptionResult).length) {
      item.selectedSubscription = await selectSubscription(
        SubscriptionType.addonForOtherBase,
        subscriptionRepository.availableAddonSubscriptionsForOtherBase(subscriptionResult),
        price,
      );
      return item;
    }
  }

  return item;
};

export const findAddonsByFileId = async (fileId: number): Promise<Subscription[]> => {
  const subscriptionResult = await lastValueFrom(mediaFileGetSubscription(fileId));
  const subscriptions = subscriptionResult
    ?.slice()
    .filter(({ dependencyType }) => dependencyType.toLowerCase() === 'addon');

  const productPrices = await lastValueFrom(
    subscriptionsGetProductPrices(subscriptions.map(({ id }) => Number(id)).filter(Boolean)),
  );

  const subscriptionInstances = subscriptions
    .map(async (subscription) => {
      // TODO use one request
      const assetResult = await lastValueFrom(
        getAssetsByFilter({
          kSql: `(and SubscriptionID = '${subscription.id}')`,
        }),
      );

      const kuxPackageAsset: KuxPackage | undefined = assetResult?.objects.length
        ? assetToType(assetResult.objects[0])
        : undefined;

      const packageAsset = kuxPackageAsset ? createPackageEntity(kuxPackageAsset) : undefined;

      if (packageAsset) {
        return new Subscription(
          subscription,
          packageAsset,
          productPrices.find((price) => price.productId === subscription.id.toString()),
        );
      }
    })
    .filter(Boolean);
  return await Promise.all(subscriptionInstances as Promise<Subscription>[]);
};

export const getParentalRatingString = <T extends KuxMediaAssetType>(data?: T): undefined | string => {
  if (!data) return undefined;
  if (!data.tags['ParentalRating'] || !data.tags['ParentalRating'][0]) return undefined;
  const parseRating = data.tags['ParentalRating'] ? parseInt(data.tags['ParentalRating'][0], 10) : 0;

  return parseRating < 0 ? undefined : data.tags['ParentalRating'][0];
};

export const addMosaicProgramsToPrograms = async (programs: Program[], loadLinears?: boolean): Promise<Program[]> => {
  const mosaicExternalIds = programs
    .map((item: Program) => {
      if (
        item.data.mosaicInfo?.isMosaicEvent === '1' &&
        (item.data.mosaicInfo?.type === 'hidden' || item.data.mosaicInfo?.type === 'video') &&
        item.data.mosaicInfo?.mosaicProgramExternalId
      ) {
        return item.data.mosaicInfo.mosaicProgramExternalId;
      }
    })
    .filter(Boolean);

  if (mosaicExternalIds.length) {
    const mosaicProgramTypes = await lastValueFrom(
      getAssetsByExternalIds<KuxProgramType>(mosaicExternalIds as string[]),
    );
    let mosaicPrograms: Program[] | undefined;

    if (mosaicProgramTypes && mosaicProgramTypes.length) {
      mosaicPrograms = mosaicProgramTypes.map((item) => item && creatorEntityStore(item)).filter(Boolean) as Program[];
    }

    if (mosaicPrograms) {
      return programs.map((program) => {
        const mosaicProgram = mosaicPrograms?.find(
          (mosaic) => mosaic?.data.externalId === program.data.mosaicInfo?.mosaicProgramExternalId,
        );

        if (mosaicProgram) {
          program.mosaicProgram = mosaicProgram;
        }

        return program;
      });
    }
  }

  return programs;
};

export const addRentalAttribute = <T extends PlayableAsset>(items: T[], value: boolean): T[] => {
  items.forEach((item) => {
    // TODO fix RentalsRailStoreV2 extends RailStoreV2<EntitiesType> and remove condition
    if (item instanceof VodAsset) {
      item.setRentalAttribute?.(value);
    }
  });
  return items;
};

export const addPurchasableAttribute = <T extends PlayableAsset>(items: T[], value: boolean): T[] => {
  items.forEach((item) => {
    if (item instanceof VodAsset) {
      item.setPurchasableAttribute(value);
    }
  });
  return items;
};
